import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import MetaChip from "../../../../../components/UIToolkit/Badges/MetaChip";
import { HighlightedText, type HighlightSpan, segmentsFromSpans } from "../../../../../components/UIToolkit/molecules/HighlightedText";
import { InlineHint } from "../../../../../components/UIToolkit/molecules/InlineHint";
import { ALPHA, FONT_SIZE, RADIUS } from "../../../../../theme/tokens";
import { AIRTABLE_EXTRACTION_LABEL_KEY, AIRTABLE_EXTRACTION_TONE, valueLines } from "./airtableImportLabels";
import type { AirtableDiffRow } from "./airtableImportTypes";

type Props = {
	row: AirtableDiffRow;
	/** The RENDERED row's identity — two rows can share a `fieldKey`, so test ids cannot use it. */
	rowId: string;
};

/** A row carries evidence worth expanding when there is a passage to read or a finding to report. */
export function hasSourceEvidence(row: AirtableDiffRow): boolean {
	return !!row.sourceText || row.spans.length > 0 || !!row.extraction;
}

/**
 * The expanded half of an extracted row: the Airtable passage the value was read out of, with the
 * exact runs the model used highlighted, beside what it concluded.
 *
 * This is the row's evidence, and it is why there is no accept-all: the check box next to an extracted
 * row is an assertion that a human read THIS text and agrees it says THAT. The passage therefore leads
 * the panel (left, wider), and the finding sits beside it as the claim being checked.
 *
 * Two shapes it must not get wrong:
 * - **A passage with NO spans is normal**, not an edge case — roughly a third of prose cells are
 *   structured JSON blobs, where a value like `"7a-3p"` can repeat and the backend refuses to guess
 *   which occurrence a span meant. The passage renders plain; it does not collapse to an empty panel.
 * - **`spans` index into `row.sourceText` directly.** A span's own `sourceText` is the EXCERPT
 *   (`row.sourceText.slice(start, end)`), never the passage — and for a merged row the passage is
 *   already the contributing cells joined by a blank line, with offsets shifted onto that join, so
 *   nothing here rebases anything.
 */
export const AirtableSourceEvidence: FC<Props> = ({ row, rowId }) => {
	const { t } = useTranslation();
	const passage = row.sourceText ?? "";
	// VERIFY the backend's own invariant — `row.sourceText.slice(start, end) === span.sourceText` — and
	// drop any span that fails it. The offsets are computed server-side, and for a merged row they are
	// shifted onto a joined string; if that arithmetic ever drifts by a cell, the highlight lands on the
	// wrong words with an accept checkbox beside it, which is a false claim about where a value came
	// from and the worst failure this feature has. Dropping degrades to the unhighlighted passage, which
	// is already a first-class state here (a structured JSON cell renders exactly that way).
	const spans: HighlightSpan[] = row.spans.filter((span) => passage.slice(span.start, span.end) === span.sourceText).map((span) => [span.start, span.end]);
	const extraction = row.extraction ?? null;
	// Keys include the position because an extracted value's lines can legitimately repeat.
	const incomingLines = valueLines(row.incomingValue, t).map((line, index) => ({ key: `${index}-${line}`, line }));

	// Which intake cells contributed, deduped on the COLUMN and in first-appearance order. On a merged
	// row the passage is several cells joined into one block of prose, so without this the reviewer
	// cannot tell that two different columns produced the one rule list they are being asked to accept.
	//
	// First label wins per column rather than last: a column's label is expected to be constant across
	// its spans, and if a diff ever disagrees with itself, showing a stable first answer beats letting
	// whichever span happened to sort last rename the column.
	// Deduped on the COLUMN, first-appearance order, first label per column wins. No `i18n.exists`
	// guard: it silently dropped every chip while the `airtable.import.source.*` keys were missing from
	// the catalog, which is exactly how a missing-translation bug hides instead of showing up as an
	// obviously wrong string. The keys are shipped; if one is ever absent, i18next echoing it is the
	// visible failure we want.
	const contributingColumns = row.spans.reduce<[string, string][]>((columns, span) => {
		if (!columns.some(([fieldId]) => fieldId === span.sourceFieldId)) columns.push([span.sourceFieldId, span.sourceLabelKey]);
		return columns;
	}, []);

	return (
		<Box
			data-testid={`airtable-evidence-${rowId}`}
			sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 1.5, p: 1.5, height: "100%", minWidth: 0, alignItems: "stretch" }}
		>
			<Box sx={{ flex: "2 1 0", minWidth: 0, display: "flex", flexDirection: "column", gap: 0.75, minHeight: 0 }}>
				<Typography
					component="span"
					sx={{ fontSize: FONT_SIZE.LABEL_XS, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "text.secondary" }}
				>
					{t("department.config.airtable.evidence.sourceText")}
				</Typography>
				{contributingColumns.length > 0 && (
					<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
						{contributingColumns.map(([fieldId, labelKey]) => (
							<MetaChip key={fieldId} label={t(labelKey)} tone="neutral" size="small" dataTestId={`airtable-evidence-column-${rowId}-${fieldId}`} />
						))}
					</Box>
				)}
				{passage ? (
					<Box
						data-testid={`airtable-evidence-source-${rowId}`}
						sx={(theme) => ({
							flex: 1,
							minHeight: 0,
							overflowY: "auto",
							p: 1.25,
							borderRadius: `${RADIUS.CARD}px`,
							border: `1px solid ${alpha(theme.palette.primary.main, ALPHA.SELECTED_OVERLAY)}`,
							backgroundColor: alpha(theme.palette.primary.main, ALPHA.HOVER_OVERLAY),
							fontSize: FONT_SIZE.EMPHASIS,
							lineHeight: 1.6,
							color: "text.primary",
							// `pre-wrap` is load-bearing on a merged row: the blank line joining two intake cells
							// is what visually separates them, and collapsing it would run two cells' prose together.
							whiteSpace: "pre-wrap",
							wordBreak: "break-word",
						})}
					>
						<HighlightedText segments={segmentsFromSpans(passage, spans)} variant="mark" />
					</Box>
				) : (
					<InlineHint tone="neutral" dataTestId={`airtable-evidence-no-source-${rowId}`}>
						{t("department.config.airtable.evidence.noSourceText")}
					</InlineHint>
				)}
				{passage && spans.length === 0 && (
					// Say WHY nothing is highlighted, so an unhighlighted passage reads as an answer rather
					// than as highlighting that failed.
					<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: "text.secondary", fontStyle: "italic" }} data-testid={`airtable-evidence-nospans-${rowId}`}>
						{t("department.config.airtable.evidence.noSpans")}
					</Typography>
				)}
			</Box>

			<Box sx={{ flex: "1 1 0", minWidth: 0, display: "flex", flexDirection: "column", gap: 0.75, minHeight: 0 }}>
				<Typography
					component="span"
					sx={{ fontSize: FONT_SIZE.LABEL_XS, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "text.secondary" }}
				>
					{t("department.config.airtable.evidence.extractedValue")}
				</Typography>
				{extraction && (
					<MetaChip
						label={t(AIRTABLE_EXTRACTION_LABEL_KEY[extraction])}
						tone={AIRTABLE_EXTRACTION_TONE[extraction]}
						size="small"
						sx={{ alignSelf: "flex-start" }}
						dataTestId={`airtable-evidence-state-${rowId}`}
					/>
				)}
				<Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "flex", flexDirection: "column", gap: 0.25, overflowY: "auto", minHeight: 0 }}>
					{incomingLines.map(({ key, line }) => (
						<Box key={key} component="li" sx={{ fontSize: FONT_SIZE.SECONDARY, lineHeight: 1.45, color: "text.secondary", wordBreak: "break-word" }}>
							{line}
						</Box>
					))}
				</Box>
				{extraction === "partial" && (
					// `missingFields` are i18n KEYS (mostly existing Settings column labels), so each is
					// translated before joining — rendering them raw prints
					// `department.config.rates.overtime.col.rate` at the reviewer.
					//
					// No `i18n.exists` fallback here on purpose. The model's names ARE free text, but the
					// backend already maps an unrecognised one onto `missing.unspecified` before it reaches
					// the wire, so a second fallback would only ever fire for a key the frontend failed to
					// ship — and silently paper over it, which is precisely how the missing
					// `airtable.import.source.*` keys stayed invisible until review caught them.
					<InlineHint tone="warning" dataTestId={`airtable-evidence-partial-${rowId}`}>
						{t("department.config.airtable.evidence.partial.body", {
							fields: row.missingFields.map((key) => t(key)).join(", "),
						})}
					</InlineHint>
				)}
			</Box>
		</Box>
	);
};
